import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Stack,
} from '@mui/material'
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material'

import { CreditPackagesSection } from './CreditPachageSection'
import { CreditsIcon } from './utils/icons'
import { PaymentMethodsSection } from './PaymentMethodsSection'
import { formatCurrency } from './utils/formatCurrency'

import { useUserPaymentMethods } from '@/hooks/useUserPaymentMethods'
import { useCreditPackagesPrices } from '@/hooks/useCreditPackagesPrices'
import { usePurchaseTopOffCredits } from '@/hooks/usePurchaseTopOffCredits'

// Constants
const DIALOG_STYLES = {
  '& .MuiDialog-paper': {
    width: '640px',
    borderRadius: '12px',
    background: '#FFF',
    boxShadow: '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
    height: 'auto',
  },
}

// Main Component
function CreditTopOffModal({
  open,
  onClose,
  title = 'You Need More Credits to Assign This Activity',
  subtitle = 'You need more credits to assign this activity. Purchase credits to top off your account.',
}) {
  // State
  const [selectedPackage, setSelectedPackage] = useState(null)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
  const [showAddCard, setShowAddCard] = useState(false)
  const [purchaseError, setPurchaseError] = useState(null)
  const [purchaseSuccess, setPurchaseSuccess] = useState(false)
  const [temporaryPaymentMethods, setTemporaryPaymentMethods] = useState([])

  // Hooks
  const {
    data: paymentMethodsData,
    isLoading: loadingPaymentMethods,
    refetch: refetchPaymentMethods,
  } = useUserPaymentMethods()
  const { data: { product, prices } = {}, isPending: loadingPackages } = useCreditPackagesPrices()
  const { mutate: purchaseCredits, isPending: purchasingCredits } = usePurchaseTopOffCredits()

  const paymentMethods = paymentMethodsData?.paymentMethods || []

  // Combine saved and temporary payment methods
  const allPaymentMethods = [...paymentMethods, ...temporaryPaymentMethods]

  // Automatically select default or first payment method
  useEffect(() => {
    if (allPaymentMethods?.length > 0 && !selectedPaymentMethod) {
      setSelectedPaymentMethod(allPaymentMethods[0])
    }
  }, [allPaymentMethods, selectedPaymentMethod])

  useEffect(() => {
    if (prices?.length > 0 && !selectedPackage) {
      setSelectedPackage(prices[0])
    }
  }, [prices, selectedPackage])

  // Event handlers
  const handleAddPaymentMethod = () => {
    setShowAddCard(true)
    // No need to create SetupIntent - we'll use mode: 'setup'
  }

  const handleSetupSuccess = async (paymentMethod, saveCard) => {
    setShowAddCard(false)

    // Format the payment method to match your existing structure
    const formattedPaymentMethod = {
      id: paymentMethod.id,
      brand: paymentMethod.card?.brand,
      last4: paymentMethod.card?.last4,
      exp_month: paymentMethod.card?.exp_month,
      exp_year: paymentMethod.card?.exp_year,
      billing_details: paymentMethod.billing_details,
      created: paymentMethod.created,
      isDefault: false,
      isTemporary: true, // Flag to indicate it's not saved yet
      saveCard: saveCard, // Remember if user wants to save it
    }

    // Add to temporary payment methods state
    setTemporaryPaymentMethods(prev => [...prev, formattedPaymentMethod])

    // Select it automatically
    setSelectedPaymentMethod(formattedPaymentMethod)
  }

  const handleSetupError = error => {
    console.error('Setup failed:', error)
  }

  const handleCancelAddCard = () => {
    setShowAddCard(false)
  }

  const handlePackageSelect = pkg => {
    setSelectedPackage(pkg)
  }

  const handlePaymentMethodSelect = paymentMethod => {
    setSelectedPaymentMethod(paymentMethod)
  }

  const handlePurchase = () => {
    if (!selectedPackage || !selectedPaymentMethod) return

    setPurchaseError(null)

    // Determine saveCard based on payment method type
    const saveCard = selectedPaymentMethod.isTemporary ? selectedPaymentMethod.saveCard : true // For existing payment methods, always save for future use

    purchaseCredits(
      {
        priceId: selectedPackage.id,
        paymentMethodId: selectedPaymentMethod.id,
        saveCard: saveCard,
      },
      {
        onSuccess: data => {
          console.log('Purchase successful:', data)
          setPurchaseSuccess(true)
          setTimeout(() => {
            onClose()
          }, 2000)
        },
        onError: error => {
          console.error('Purchase failed:', error)
          setPurchaseError(error.message || 'Failed to purchase credits. Please try again.')
        },
      },
    )
  }

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedPackage(null)
      setSelectedPaymentMethod(null)
      setShowAddCard(false)
      setPurchaseError(null)
      setPurchaseSuccess(false)
      setTemporaryPaymentMethods([]) // Clear temporary payment methods
    }
  }, [open])

  // Computed values
  const canProceed = selectedPackage && selectedPaymentMethod && !purchasingCredits

  return (
    <Dialog fullWidth maxWidth="md" open={open} sx={DIALOG_STYLES} onClose={onClose}>
      <DialogTitle sx={{ position: 'relative', p: 0 }}>
        <Stack direction="row" gap="16px" p="24px 24px 20px 24px">
          <CreditsIcon />
          <Stack>
            <Typography color="#101828" fontSize={18} fontWeight={600} lineHeight="28px">
              {title}
            </Typography>
            <Typography color="#475467" fontSize={14} fontWeight={400} lineHeight="20px">
              {subtitle}
            </Typography>
          </Stack>
          <IconButton sx={{ position: 'absolute', right: 15, top: 15 }} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          padding: '24px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '20px',
        }}
      >
        <PaymentMethodsSection
          isLoading={loadingPaymentMethods}
          paymentMethods={allPaymentMethods}
          selectedPaymentMethod={selectedPaymentMethod}
          showAddCard={showAddCard}
          onAddPaymentMethod={handleAddPaymentMethod}
          onCancelAddCard={handleCancelAddCard}
          onPaymentMethodSelect={handlePaymentMethodSelect}
          onSetupError={handleSetupError}
          onSetupSuccess={handleSetupSuccess}
        />

        <CreditPackagesSection
          isLoading={loadingPackages}
          prices={prices}
          purchaseError={purchaseError}
          purchaseSuccess={purchaseSuccess}
          selectedPackage={selectedPackage}
          onPackageSelect={handlePackageSelect}
        />
      </DialogContent>

      <DialogActions
        sx={{
          display: 'flex',
          padding: '24px 24px 24px 24px',
          alignItems: 'flex-start',
          gap: '12px',
        }}
      >
        <Button fullWidth color="secondary" variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          fullWidth
          disabled={!canProceed || purchasingCredits || purchaseSuccess}
          startIcon={purchasingCredits ? <CircularProgress size={20} /> : null}
          variant="contained"
          onClick={handlePurchase}
        >
          {purchasingCredits
            ? 'Processing...'
            : purchaseSuccess
              ? 'Purchase Complete!'
              : selectedPackage
                ? `Purchase ${selectedPackage.metadata?.credit_amount} Credits for ${formatCurrency(selectedPackage.unit_amount)}`
                : 'Select Package'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreditTopOffModal
